import React, { useState, useEffect, useContext } from 'react'
import { SettingsLayout } from '@/components/layouts/settings-layout'
import { useTranslation } from 'react-i18next'
import { css } from '@emotion/core'
import { TabContainer } from '@/components/organisms/settings/tab-container'
import { EditFooter } from '@/components/organisms/edit-footer'
import { SurveyTab } from '@/components/molecules/settings/survey-tab'
import * as Api from '@/apis/aipass'
import { useHistory } from 'react-router-dom'
import { useErrorHandler } from '@/hooks/use-error-handler'
import { AccountContext } from '@/contexts/account'
import { LoadingFull } from '@/components/molecules/loading-full'
import { ToggleButton } from '@/components/molecules/settings/toggle-button'

const formContainerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '50%',
  padding: 24,
  gap: 16,
})

const formFieldStyle = css({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
})

const labelStyle = css({
  display: 'block',
  color: '#676767',
  fontSize: 12,
  fontWeight: 'bold',
  letterSpacing: '0.6px',
  textAlign: 'left',
  opacity: 1,
  marginBottom: 12,
})

const requiredMarkStyle = css({
  color: '#676767',
  fontSize: 8,
  fontWeight: 'bold',
  letterSpacing: '0.4px',
  textAlign: 'left',
  opacity: 1,
  position: 'relative',
  top: '-4px',
  left: '2px',
})

const inputStyle = css({
  width: '100%',
  height: 32,
  border: '1px solid #CCCCCC',
  borderRadius: 16,
  padding: '0 16px',
  fontSize: 12,
  backgroundColor: '#FFFFFF',
  '::placeholder': {
    color: '#CCCCCC',
  },
  '&:focus': {
    outline: 'none',
    borderColor: '#F2A40B',
  },
})

const helpTextStyle = css({
  fontSize: 10,
  color: '#676767',
  marginTop: 4,
})

export const SurveyGoogleMap: React.FC = () => {
  const { t } = useTranslation()
  const history = useHistory()
  const { errorHandler } = useErrorHandler()
  const { account } = useContext<any>(AccountContext)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true)
  const [isMounted, setIsMounted] = useState<boolean>(true)
  const [googleMapEnabled, setGoogleMapEnabled] = useState<boolean>(false)
  const [googleMapUrl, setGoogleMapUrl] = useState<string>('')
  const [showTooltip, setShowTooltip] = useState<boolean>(false)
  const [isShowFooter, setIsShowFooter] = useState<boolean>(false)
  const [originalGoogleMapEnabled, setOriginalGoogleMapEnabled] = useState<boolean>(false)
  const [originalGoogleMapUrl, setOriginalGoogleMapUrl] = useState<string>('')

  const fetchGoogleMapSettings = async () => {
    try {
      const res = await Api.fetchGoogleMapSettings()
      if (!isMounted) return

      const enabled = res.googleMapEnabled || false
      const url = res.googleMapUrl || ''
      setGoogleMapEnabled(enabled)
      setGoogleMapUrl(url)
      setOriginalGoogleMapEnabled(enabled)
      setOriginalGoogleMapUrl(url)
      setIsInitialLoading(false)
    } catch (error) {
      console.error('Failed to fetch GoogleMap settings:', error)
      if (isMounted) {
        // 404の場合は新規作成として扱う
        setGoogleMapEnabled(false)
        setGoogleMapUrl('')
        setOriginalGoogleMapEnabled(false)
        setOriginalGoogleMapUrl('')
        setIsInitialLoading(false)
      }
    }
  }

  const updateGoogleMapSettings = async () => {
    try {
      setIsLoading(true)

      if (googleMapEnabled && !googleMapUrl.trim()) {
        alert(t('Please enter the Google Business Profile URL.'))
        return
      }
      // 【追加】http/httpsバリデーション
      if (googleMapEnabled && !/^https?:\/\/.+/.test(googleMapUrl.trim())) {
        alert(t('URLは http:// または https:// から始まる形式で入力してください。'))
        return
      }

      const requestData = {
        googleMapEnabled,
        googleMapUrl: googleMapEnabled ? googleMapUrl.trim() : null,
      }

      await Api.updateGoogleMapSettings(requestData)

      if (!isMounted) return

      // 保存成功時に元の値を更新
      setOriginalGoogleMapEnabled(googleMapEnabled)
      setOriginalGoogleMapUrl(googleMapUrl)
      setIsShowFooter(false)
    } catch (e) {
      if (isMounted) {
        errorHandler(e)
      }
    } finally {
      if (isMounted) {
        setIsLoading(false)
      }
    }
  }

  const handleCancel = () => {
    // 元の状態に戻す
    setGoogleMapEnabled(originalGoogleMapEnabled)
    setGoogleMapUrl(originalGoogleMapUrl)
    setIsShowFooter(false)
  }

  const handleGoogleMapEnabledChange = () => {
    const newValue = !googleMapEnabled
    setGoogleMapEnabled(newValue)
    setIsShowFooter(true)
    // URLは保持する（トグルをOFFにしてもURLは消さない）
  }

  useEffect(() => {
    if (account && account?.hotel) {
      fetchGoogleMapSettings()
    }

    return () => {
      setIsMounted(false)
    }
  }, [account])

  const footerContent = isShowFooter ? <EditFooter onCancel={handleCancel} onSave={updateGoogleMapSettings} /> : undefined

  if (isInitialLoading) {
    return <LoadingFull isLoading={isInitialLoading} />
  }

  return (
    <SettingsLayout loading={isLoading || isInitialLoading} footerContent={footerContent}>
      <TabContainer tabComponent={<SurveyTab currentTab="Google Map" />} isShowFooter={isShowFooter}>
        <div css={formContainerStyle}>
          <div css={formFieldStyle}>
            <label
              css={[
                labelStyle,
                css`
                  display: flex;
                  alignitems: center;
                  marginbottom: 12;
                `,
              ]}
            >
              <span>{t('Enable Google Map review guidance')}</span>

              {/* ツールチップ */}
              <div
                onClick={e => e.stopPropagation()}
                onMouseEnter={() => setShowTooltip(true)}
                onMouseLeave={() => setShowTooltip(false)}
                style={{
                  position: 'relative',
                  display: 'inline-flex',
                  alignItems: 'center',
                  marginLeft: '8px',
                  marginTop: '-3px',
                }}
              >
                <img src={require('@/static/images/help.svg')} alt="help" width={18} height={18} style={{ cursor: 'help' }} />

                {/* スタイリッシュツールチップ */}
                {showTooltip && (
                  <div
                    style={{
                      position: 'absolute',
                      top: '130%',
                      left: '-60px',
                      background: '#fff',
                      color: '#676767',
                      fontSize: 12,
                      lineHeight: 1.4,
                      fontWeight: 'normal',
                      padding: '12px 16px',
                      borderRadius: 4,
                      whiteSpace: 'pre-line',
                      boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                      border: '1px solid #e0e0e0',
                      zIndex: 999,
                      width: 280,
                      textAlign: 'left',
                      transition: 'opacity 0.2s ease',
                    }}
                  >
                    高評価のゲストを中心に、Googleマップでのレビュー誘導を促すことができます。
                    <div style={{ marginTop: '8px', textAlign: 'center', padding: '0 16px' }}>
                      <img
                        src={require('@/static/images/surveys/survey_popup.png')}
                        alt="Survey popup example"
                        style={{
                          maxWidth: '100%',
                          height: 'auto',
                          borderRadius: '4px',
                        }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </label>

            <div
              css={css`
                margintop: 8px;
              `}
            >
              <ToggleButton
                label={{ on: t('enable'), off: t('disable') }}
                value={googleMapEnabled}
                onChange={handleGoogleMapEnabledChange}
              />
            </div>
          </div>

          {googleMapEnabled && (
            <div css={formFieldStyle}>
              <label htmlFor="googleMapUrl" css={labelStyle}>
                {t('Google Business Profile URL')}
                <span css={requiredMarkStyle}>※</span>
              </label>
              <input
                type="url"
                id="googleMapUrl"
                value={googleMapUrl}
                onChange={e => {
                  setGoogleMapUrl(e.target.value)
                  setIsShowFooter(true)
                }}
                css={inputStyle}
                placeholder="https://g.co/kgs/xxxxx"
              />
            </div>
          )}
        </div>
      </TabContainer>
    </SettingsLayout>
  )
}
